import React from "react";
import { Box, Typography, Skeleton, Button, useTheme } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";

export const ViewerSkeleton = () => (
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      maxWidth: "100%",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      minHeight: 280,
      gap: 2,
      px: 2,
      py: 3,
    }}
  >
    <Skeleton
      variant="text"
      width="60%"
      height={28}
      sx={{ bgcolor: "grey.300" }}
    />
    <Skeleton
      variant="text"
      width="40%"
      height={20}
      sx={{ bgcolor: "grey.300" }}
    />
    <Skeleton
      variant="rectangular"
      height={120}
      sx={{ bgcolor: "grey.300", borderRadius: 1, width: "100%" }}
    />
    <Skeleton
      variant="text"
      width="100%"
      height={20}
      sx={{ bgcolor: "grey.300" }}
    />
    <Skeleton
      variant="text"
      width="90%"
      height={20}
      sx={{ bgcolor: "grey.300" }}
    />
    <Skeleton
      variant="text"
      width="95%"
      height={20}
      sx={{ bgcolor: "grey.300" }}
    />
    <Skeleton
      variant="rectangular"
      height={200}
      sx={{ bgcolor: "grey.300", borderRadius: 1, width: "100%", mt: 1 }}
    />
  </Box>
);

export const ViewerErrorState = ({
  title = "Couldn't load this document",
  message = "The file may be missing or unavailable. Try again in a moment.",
  onRetry,
}) => {
  const theme = useTheme();
  const customColors = theme.customColors || {};
  const borderMain = customColors.border?.main ?? "#E8EAED";
  const textSecondary = customColors.text?.secondary ?? "#6B7280";

  return (
    <Box
      role="alert"
      aria-live="polite"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        minHeight: "60vh",
        width: "100%",
        p: 2,
      }}
    >
      <Box
        sx={{
          backgroundColor: customColors.background?.tableRow ?? "#F3F4F6",
          boxShadow: customColors.shadow?.card ?? "0 1px 3px rgba(0,0,0,0.1)",
          border: `1px solid ${borderMain}`,
          borderRadius: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 220,
          maxWidth: 520,
          width: "100%",
          px: 3,
          py: 4,
          gap: 2,
        }}
      >
        <ErrorOutline
          sx={{
            color: customColors.icons?.secondary ?? textSecondary,
            fontSize: 40,
            mb: 0.5,
          }}
          aria-hidden
        />
        <Typography variant="h6" sx={{ textAlign: "center" }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", maxWidth: 360 }}>
          {message}
        </Typography>
        {onRetry && (
          <Button
            variant="contained"
            color="primary"
            onClick={onRetry}
            aria-label="Try loading the document again"
            sx={{
              mt: 1,
              textTransform: "none",
              px: 3,
              py: 1.5,
              fontWeight: 600,
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: theme.customColors?.button?.hover,
              },
              "&:disabled": {
                backgroundColor: theme.palette.action.disabledBackground,
                color: theme.palette.action.disabled,
              },
            }}
          >
            Try again
          </Button>
        )}
      </Box>
    </Box>
  );
};
